/**
 * EditCartItemDialog
 *
 * Lets the salesperson tweak any cart line AFTER it's been added — most
 * commonly used for quick-add (custom) items where they need to fix the
 * description, brand, code, size, rate or thickness that will appear on
 * the PDF.
 *
 * Behaviour:
 *  • Rate-type aware: when the line is sqft-priced (Plywood / Veneers /
 *    Doors / MDF / WPC/PVC / Blockboard) AND unit isn't "SQ FT", the
 *    dialog auto-expands rate × sqft_per_sheet into `calculated_rate`
 *    just like Quick Add does. So changing rate or size keeps the math
 *    consistent.
 *  • Brand visibility toggle ("show on customer PDF") is honoured.
 *  • Size is OPTIONAL — empty size renders an empty size column.
 */
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { SQFT_CATEGORIES, sizeToSqft } from '../../lib/store';

const COMMON_SIZES = ['8x4', '7x4', '6x4', '8x3', '7x3', '6x3', '10x4'];

const RATE_TYPE_OPTIONS = [
  { value: 'per piece', label: 'Per Piece' },
  { value: 'per sheet', label: 'Per Sheet' },
  { value: 'per sq ft', label: 'Per Sq Ft' },
  { value: 'per box', label: 'Per Box' },
  { value: 'per bundle', label: 'Per Bundle' },
  { value: 'per set', label: 'Per Set' },
  { value: 'per kg', label: 'Per Kg' },
  { value: 'per running feet', label: 'Per Running Feet' },
  { value: 'per ltr', label: 'Per Litre' },
];

const UNIT_OPTIONS = [
  { value: 'NOS', label: 'NOS (Piece)' },
  { value: 'SHT', label: 'SHEET' },
  { value: 'SQ FT', label: 'SQ FT' },
  { value: 'BOX', label: 'BOX' },
  { value: 'BDL', label: 'BUNDLE' },
  { value: 'SET', label: 'SET' },
  { value: 'KG', label: 'KG' },
  { value: 'RFT', label: 'RFT' },
];

export function EditCartItemDialog({ open, onOpenChange, item, updateItem }) {
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!item) {
      setForm(null);
      return;
    }
    setForm({
      brand: item.brand || '',
      description: item.description || '',
      // iter 114: explicit Quick Add Description (renamed from Finish).
      // Read both keys so legacy items still surface their old finish.
      description_text: item.description_text || item.finish || '',
      product_code: item.product_code || '',
      thickness: item.thickness || '',
      size: item.size || '',
      rate: item.rate || 0,
      rate_type: item.rate_type || '',
      unit: item.unit || 'NOS',
      show_brand: item.show_brand !== false,
      // Veneer-only fields
      species: item.species || '',
      finish: item.finish || '',
    });
  }, [item]);

  if (!form || !item) return null;

  const cat = item.main_category || 'Others';
  const isSqftCategory = SQFT_CATEGORIES.has(cat);
  const isVeneerLike = ['Laminates', 'Veneers', 'Highlighters'].includes(cat);

  const handleSave = () => {
    const rate = parseFloat(form.rate) || 0;
    if (rate <= 0) {
      toast.error('Rate must be greater than 0');
      return;
    }
    // Recompute calculated_rate the same way `addItem` / `addQuickVeneer` do.
    //
    // BUG FIX (iter 114): previously we gated `sqftPerSheet` on
    // `isSqftCategory` — that broke Laminate Quick Add items saved with
    // UOM="Per Sq Ft" because Laminates aren't in SQFT_CATEGORIES. The
    // edit dialog would skip the sqft multiplication and the line total
    // silently collapsed to rate × qty (e.g., ₹140 × 2 = ₹280 instead of
    // ₹140 × 32 × 2 = ₹8,960 on an 8×4 sheet).
    //
    // Source of truth is now `rate_type === 'per sq ft'`, exactly like
    // `addQuickVeneer` does it. We expand whenever the rate is declared
    // as per-sqft AND a parseable size exists AND the unit isn't already
    // SQ FT (which means qty is already loose sqft).
    const unitUpper = String(form.unit || '').toUpperCase();
    const isSqftUnit = unitUpper === 'SQ FT' || unitUpper === 'SQFT' || unitUpper === 'SQ.FT';
    const sqftPerSheet = form.size ? sizeToSqft(form.size, cat) : 0;
    const isSqftRate = (form.rate_type || '').toLowerCase().includes('sq ft');
    const expandToPerSheet = (isSqftRate || (isSqftCategory && !isSqftUnit)) && !isSqftUnit && sqftPerSheet > 0;
    const calculated_rate = expandToPerSheet ? rate * sqftPerSheet : rate;

    updateItem(item.cart_id, {
      brand: form.brand,
      description: form.description,
      // iter 114: keep both `description_text` (canonical for PDF) and
      // `finish` (legacy column) in sync so a re-render uses the right
      // string everywhere.
      description_text: form.description_text,
      finish: form.description_text || form.finish,
      product_code: form.product_code,
      thickness: form.thickness,
      size: form.size,
      rate,
      rate_type: form.rate_type,
      unit: form.unit,
      show_brand: form.show_brand,
      species: form.species,
      calculated_rate,
    });
    toast.success('Item updated');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-heda-line bg-white sm:rounded-3xl sm:max-w-lg" data-testid="edit-cart-item-dialog">
        <DialogHeader>
          <DialogTitle>Edit item</DialogTitle>
          <DialogDescription>
            Changes apply to this quotation only. Brand &amp; code always show
            on the internal PDF; toggle &quot;Show brand to customer&quot; to make
            them appear on the customer PDF too.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {/* Brand always at the top (any category). Hint clarifies that
              internal PDF always shows it; customer PDF only if the
              toggle below is on. */}
          <div>
            <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">
              Brand <span className="text-[10px] text-heda-mute">(internal PDF always shows; customer PDF only if toggled below)</span>
            </Label>
            <Input
              value={form.brand}
              onChange={(e) => setForm(f => ({ ...f, brand: e.target.value }))}
              placeholder="e.g., Century"
              className="rounded-xl bg-heda-cream/50 mt-1 text-sm"
              data-testid="edit-item-brand"
            />
          </div>

          {/* Category-aware PDF-composing fields. For Laminates/
              Highlighters we surface ①Description ②Code ③Thickness
              with order markers, matching the Quick Add UI exactly. For
              Veneers it's ①Species ②Code. For other categories the
              free-form Description is the line as it appears in cart. */}
          {['Laminates', 'Highlighters'].includes(cat) ? (
            <>
              <div>
                <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute flex items-center gap-1">
                  <span className="font-semibold text-heda-olive-dark">①</span>
                  <span>Description</span>
                  <span className="text-[10px] text-heda-mute">(shows on PDF, optional)</span>
                </Label>
                <Input
                  value={form.description_text}
                  onChange={(e) => setForm(f => ({ ...f, description_text: e.target.value }))}
                  placeholder="e.g., Matte, Walnut Burl, Backlit…"
                  className="rounded-xl bg-heda-cream/50 mt-1 text-sm"
                  data-testid="edit-item-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute flex items-center gap-1">
                    <span className="font-semibold text-heda-olive-dark">②</span>
                    <span>Code *</span>
                  </Label>
                  <Input
                    value={form.product_code}
                    onChange={(e) => setForm(f => ({ ...f, product_code: e.target.value.toUpperCase() }))}
                    className="rounded-xl bg-heda-cream/50 mt-1 text-sm font-mono uppercase"
                    data-testid="edit-item-code"
                  />
                </div>
                <div>
                  <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute flex items-center gap-1">
                    <span className="font-semibold text-heda-olive-dark">③</span>
                    <span>Thickness</span>
                    <span className="text-[10px] text-heda-mute">(optional)</span>
                  </Label>
                  <Input
                    value={form.thickness}
                    onChange={(e) => setForm(f => ({ ...f, thickness: e.target.value.toUpperCase() }))}
                    placeholder="e.g., 1MM, 2MM"
                    className="rounded-xl bg-heda-cream/50 mt-1 text-sm uppercase"
                    data-testid="edit-item-thickness"
                  />
                </div>
              </div>
            </>
          ) : isVeneerLike ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute flex items-center gap-1">
                  <span className="font-semibold text-heda-olive-dark">①</span>
                  <span>{cat === 'Veneers' ? 'Species' : 'Description'}</span>
                </Label>
                <Input
                  value={cat === 'Veneers' ? form.species : form.description_text}
                  onChange={(e) => setForm(f => ({
                    ...f,
                    ...(cat === 'Veneers' ? { species: e.target.value } : { description_text: e.target.value })
                  }))}
                  className="rounded-xl bg-heda-cream/50 mt-1 text-sm"
                  data-testid="edit-item-description"
                />
              </div>
              <div>
                <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute flex items-center gap-1">
                  <span className="font-semibold text-heda-olive-dark">②</span>
                  <span>Product Code</span>
                </Label>
                <Input
                  value={form.product_code}
                  onChange={(e) => setForm(f => ({ ...f, product_code: e.target.value.toUpperCase() }))}
                  className="rounded-xl bg-heda-cream/50 mt-1 text-sm font-mono uppercase"
                  data-testid="edit-item-code"
                />
              </div>
            </div>
          ) : (
            <div>
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Description <span className="text-[10px] text-heda-mute">(shows on PDF)</span></Label>
              <Input
                value={form.description}
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                className="rounded-xl bg-heda-cream/50 mt-1 text-sm"
                data-testid="edit-item-description"
              />
            </div>
          )}

          {/* Thickness for Plywood-family categories (existing behaviour).
              For Laminates/Highlighters thickness is already shown above
              with order marker ③. For Veneers we keep it hidden. */}
          {!['Laminates', 'Highlighters', 'Veneers'].includes(cat) && (
            <div>
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Thickness</Label>
              <Input
                value={form.thickness}
                onChange={(e) => setForm(f => ({ ...f, thickness: e.target.value.toUpperCase() }))}
                placeholder="e.g., 18MM"
                className="rounded-xl bg-heda-cream/50 mt-1 text-sm uppercase"
                data-testid="edit-item-thickness"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Size (optional)</Label>
              <Input
                list="edit-item-size-suggestions"
                value={form.size}
                onChange={(e) => setForm(f => ({ ...f, size: e.target.value }))}
                placeholder="e.g., 8x4 or leave blank"
                className="rounded-xl bg-heda-cream/50 mt-1 text-sm"
                data-testid="edit-item-size"
              />
              <datalist id="edit-item-size-suggestions">
                {COMMON_SIZES.map(s => <option key={s} value={s} />)}
              </datalist>
            </div>
            <div>
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Unit</Label>
              <Select value={form.unit} onValueChange={(v) => setForm(f => ({ ...f, unit: v }))}>
                <SelectTrigger className="rounded-xl bg-heda-cream/50 mt-1 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {UNIT_OPTIONS.map(u => (
                    <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Rate Type</Label>
              <Select value={form.rate_type} onValueChange={(v) => setForm(f => ({ ...f, rate_type: v }))}>
                <SelectTrigger className="rounded-xl bg-heda-cream/50 mt-1 text-sm"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {RATE_TYPE_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Rate (₹)</Label>
              <Input
                type="number"
                value={form.rate}
                onChange={(e) => setForm(f => ({ ...f, rate: e.target.value }))}
                className="rounded-xl bg-heda-cream/50 mt-1 text-sm font-mono"
                data-testid="edit-item-rate"
              />
            </div>
          </div>

          {isSqftCategory && form.size && form.rate_type === 'per sq ft' && form.unit !== 'SQ FT' && (
            <p className="text-[11px] text-heda-sub bg-heda-cream/60 rounded-lg px-2 py-1">
              ✓ Per-sheet rate after sqft expansion: ₹{(parseFloat(form.rate || 0) * sizeToSqft(form.size, cat)).toFixed(2)}
              <span className="text-heda-mute"> ({form.rate} × {sizeToSqft(form.size, cat)} sqft)</span>
            </p>
          )}

          <div className="flex items-center justify-between rounded-lg border border-heda-line px-3 py-2">
            <div>
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Show brand on customer PDF</Label>
              <p className="text-[10px] text-heda-mute">Internal PDF always shows the brand.</p>
            </div>
            <Switch
              checked={form.show_brand}
              onCheckedChange={(v) => setForm(f => ({ ...f, show_brand: v }))}
              data-testid="edit-item-show-brand"
            />
          </div>

          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl flex-1 h-9 text-sm bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="rounded-xl flex-1 bg-heda-ink hover:bg-black text-white h-9 text-sm"
              data-testid="edit-item-save"
            >
              Save changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default EditCartItemDialog;
